/* eslint-disable max-params */
import type {
  DocumentReferencePreview,
  DocumentVersion,
  UserDocument,
  UserDocumentParticipantRole,
} from "../models"

import { BACKEND_URI, fetchWithAuthRetry, getHeaders } from "./_shared"

export type CreateDocumentPayload = {
  title?: string
  content?: string
  contentFormat?: string
  projectId?: string
  sourceSessionId?: string
  sourceMessageId?: string
  artifactType?: string
  artifactMeta?: Record<string, unknown>
}

export type UpdateDocumentPayload = {
  title?: string
  content?: string
  contentFormat?: string
  projectId?: string
  /** Chat the canvas was saved from. The server refuses the update when that chat holds personal content. */
  sourceSessionId?: string
  edit_source?: "user" | "ai" | "system"
  edit_preview?: string
  editor?: {
    oid?: string
    displayName?: string
  }
  create_version?: boolean
}

export type DocumentAiAssistTaskMode = "edit" | "explain"

export type DocumentAiAssistPayload = {
  instruction: string
  task_mode?: DocumentAiAssistTaskMode
  selected_text?: string
  selection_start?: number
  selection_end?: number
  document_content_snippet?: string
  model_id?: string
  session_id?: string
  /** Module route the canvas runs under; the server enforces its access and locks. */
  route_id?: string
  /** Ignored by the server without a route_id, and when the route locks web search. */
  use_web_search?: boolean
}

export type DocumentAiAssistResponse = {
  success: boolean
  task_mode: DocumentAiAssistTaskMode
  instruction: string
  document_id: string
  document_title?: string
  selected_text?: string
  edited_text?: string
  explanation?: string
  raw_output?: string
  context?: Record<string, unknown>
  document_references?: DocumentReferencePreview[]
  finish_reason?: string | null
  tool_calls?: unknown[]
  error?: string
}

export type DocumentParticipantsResponse = {
  participants: Array<{
    oid: string
    displayName: string
    type: "user" | "group"
    role: UserDocumentParticipantRole
  }>
  currentUserRole: UserDocumentParticipantRole
  ownerUserId: string
}

export async function listDocumentsApi(
  idToken?: string,
  projectId?: string
): Promise<UserDocument[]> {
  const query = new URLSearchParams()
  if (projectId) query.set("project_id", projectId)
  const querySuffix = query.toString() ? `?${query.toString()}` : ""

  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/documents${querySuffix}`,
    {
      method: "GET",
      headers: getHeaders(idToken),
    },
    idToken
  )

  return (await response.json()) as UserDocument[]
}

export async function createDocumentApi(
  payload: CreateDocumentPayload,
  idToken?: string
): Promise<UserDocument> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/documents`,
    {
      method: "POST",
      headers: getHeaders(idToken),
      body: JSON.stringify(payload),
    },
    idToken
  )

  return (await response.json()) as UserDocument
}

export async function createDocumentFromMessageApi(
  messageId: string,
  title?: string,
  idToken?: string
): Promise<UserDocument> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/documents/from-message`,
    {
      method: "POST",
      headers: getHeaders(idToken),
      body: JSON.stringify({ messageId, title }),
    },
    idToken
  )

  return (await response.json()) as UserDocument
}

export async function getDocumentApi(documentId: string, idToken?: string): Promise<UserDocument> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/documents/${encodeURIComponent(documentId)}`,
    {
      method: "GET",
      headers: getHeaders(idToken),
    },
    idToken
  )

  return (await response.json()) as UserDocument
}

export async function updateDocumentApi(
  documentId: string,
  payload: UpdateDocumentPayload,
  idToken?: string
): Promise<UserDocument> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/documents/${encodeURIComponent(documentId)}`,
    {
      method: "PUT",
      headers: getHeaders(idToken),
      body: JSON.stringify(payload),
    },
    idToken
  )

  return (await response.json()) as UserDocument
}

export async function aiAssistDocumentApi(
  documentId: string,
  payload: DocumentAiAssistPayload,
  idToken?: string
): Promise<DocumentAiAssistResponse> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/documents/${encodeURIComponent(documentId)}/ai-assist`,
    {
      method: "POST",
      headers: getHeaders(idToken),
      body: JSON.stringify(payload),
    },
    idToken
  )

  return (await response.json()) as DocumentAiAssistResponse
}

export async function listDocumentVersionsApi(
  documentId: string,
  idToken?: string
): Promise<DocumentVersion[]> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/documents/${encodeURIComponent(documentId)}/versions`,
    {
      method: "GET",
      headers: getHeaders(idToken),
    },
    idToken
  )
  const data = (await response.json()) as { versions?: DocumentVersion[] }
  return data.versions ?? []
}

export async function deleteDocumentApi(
  documentId: string,
  idToken?: string
): Promise<{ status: string }> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/documents/${encodeURIComponent(documentId)}`,
    {
      method: "DELETE",
      headers: getHeaders(idToken),
    },
    idToken
  )

  return (await response.json()) as { status: string }
}

export async function listDocumentParticipantsApi(
  documentId: string,
  idToken?: string
): Promise<DocumentParticipantsResponse> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/documents/${encodeURIComponent(documentId)}/participants`,
    {
      method: "GET",
      headers: getHeaders(idToken),
    },
    idToken
  )

  return (await response.json()) as DocumentParticipantsResponse
}

export async function addDocumentParticipantApi(
  documentId: string,
  participant: {
    oid: string
    displayName: string
    type: "user" | "group"
    role: UserDocumentParticipantRole
  },
  idToken?: string
): Promise<UserDocument> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/documents/${encodeURIComponent(documentId)}/participants`,
    {
      method: "POST",
      headers: getHeaders(idToken),
      body: JSON.stringify({
        participant_oid: participant.oid,
        display_name: participant.displayName,
        type: participant.type,
        role: participant.role,
      }),
    },
    idToken
  )

  return (await response.json()) as UserDocument
}

export async function updateDocumentParticipantRoleApi(
  documentId: string,
  participantOid: string,
  role: UserDocumentParticipantRole,
  idToken?: string
): Promise<UserDocument> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/documents/${encodeURIComponent(documentId)}/participants/${encodeURIComponent(participantOid)}`,
    {
      method: "PUT",
      headers: getHeaders(idToken),
      body: JSON.stringify({ role }),
    },
    idToken
  )

  return (await response.json()) as UserDocument
}

export async function removeDocumentParticipantApi(
  documentId: string,
  participantOid: string,
  idToken?: string
): Promise<UserDocument> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/documents/${encodeURIComponent(documentId)}/participants/${encodeURIComponent(participantOid)}`,
    {
      method: "DELETE",
      headers: getHeaders(idToken),
    },
    idToken
  )

  return (await response.json()) as UserDocument
}

export async function transferDocumentOwnershipApi(
  documentId: string,
  newOwnerOid: string,
  idToken?: string
): Promise<{ success: boolean; newOwnerOid: string; message: string }> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/documents/${encodeURIComponent(documentId)}/transfer-ownership`,
    {
      method: "POST",
      headers: getHeaders(idToken),
      body: JSON.stringify({ newOwnerOid }),
    },
    idToken
  )

  return (await response.json()) as { success: boolean; newOwnerOid: string; message: string }
}

/** Base URL for a document's attachments; a filename is appended as `/{filename}`. */
export function documentContentBaseUrl(documentId: string): string {
  return `${BACKEND_URI}/documents/${encodeURIComponent(documentId)}/content`
}

/** URL for a document-scoped attachment blob (e.g. a shared artifact's image). */
export function documentContentUrl(documentId: string, filename: string): string {
  return `${documentContentBaseUrl(documentId)}/${encodeURIComponent(filename)}`
}

export type DocumentLockInfo = {
  userOid?: string
  displayName?: string
  expiresAt?: string
}

/** Acquire/renew the check-out edit lock. status 200 = held by us, 409 = held by another. */
export async function acquireDocumentLockApi(
  documentId: string,
  idToken?: string
): Promise<{ status: number; lock?: DocumentLockInfo }> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/documents/${encodeURIComponent(documentId)}/lock`,
    { method: "POST", headers: getHeaders(idToken) },
    idToken
  )
  const body = (await response.json()) as { lock?: DocumentLockInfo }
  return { status: response.status, lock: body.lock }
}

export async function releaseDocumentLockApi(documentId: string, idToken?: string): Promise<void> {
  await fetchWithAuthRetry(
    `${BACKEND_URI}/documents/${encodeURIComponent(documentId)}/lock`,
    { method: "DELETE", headers: getHeaders(idToken) },
    idToken
  )
}

/**
 * Release the lock as the page is being hidden/closed. Uses `keepalive` fetch —
 * the modern replacement for `navigator.sendBeacon` that still lets us send the
 * auth headers a DELETE needs — so the request survives the unload that would
 * cancel a normal fetch. Best-effort: the server-side TTL self-heals if it never
 * lands, so failures are swallowed.
 */
export function releaseDocumentLockOnUnload(documentId: string, idToken?: string): void {
  void fetch(`${BACKEND_URI}/documents/${encodeURIComponent(documentId)}/lock`, {
    method: "DELETE",
    headers: getHeaders(idToken),
    keepalive: true,
  }).catch(() => {
    /* best-effort: TTL frees the lock if this never reaches the server */
  })
}
